import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../components/MainLayout';
import { callAPI } from '../utils/apiUtils';
import { apiUrls } from '../utils/apiUrls';
import '../styles/operatingHours.css';

const ORDER_TYPES = [
    { key: 'delivery', label: 'Delivery' },
    { key: 'take_away', label: 'Takeaway' },
    { key: 'reservation', label: 'Reservation' },
];

const DAY_NUMBERS = [1, 2, 3, 4, 5, 6, 7];
const DAY_SHORT = { 1: 'Sun', 2: 'Mon', 3: 'Tue', 4: 'Wed', 5: 'Thu', 6: 'Fri', 7: 'Sat' };

// Storage format is "hh:mm a" (e.g. "11:30 am", "04:00 pm") - parsed to/from minutes-since-midnight
// so contiguous same-interval runs can be detected and collapsed into a single range for display,
// rather than showing every individual slot as its own row like restroadmin's page does.
const timeToMinutes = (time) => {
    const m = /^\s*(\d{1,2}):(\d{2})\s*(am|pm)\s*$/i.exec(time || '');
    if (!m) return null;
    let hour = parseInt(m[1], 10) % 12;
    if (m[3].toLowerCase() === 'pm') hour += 12;
    return hour * 60 + parseInt(m[2], 10);
};
const minutesToHHMM = (mins) => {
    const hh = Math.floor(mins / 60) % 24;
    const mm = mins % 60;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};
const minutesToLabel = (mins) => {
    const hh24 = Math.floor(mins / 60) % 24;
    const mm = mins % 60;
    const ap = hh24 >= 12 ? 'PM' : 'AM';
    let hh12 = hh24 % 12;
    if (hh12 === 0) hh12 = 12;
    return `${hh12}:${String(mm).padStart(2, '0')} ${ap}`;
};

// Groups a day's slots into runs that share the same gap between consecutive times and the same
// order_limit - each run renders as one chip ("11:30 AM - 9:30 PM every 15 min") instead of one
// chip per individual slot.
const collapseSlots = (slots) => {
    const sorted = slots
        .map((s) => ({ ...s, minutes: timeToMinutes(s.time) }))
        .filter((s) => s.minutes !== null)
        .sort((a, b) => a.minutes - b.minutes);

    const ranges = [];
    let current = null;
    for (const slot of sorted) {
        const gap = current ? slot.minutes - current.lastMinutes : null;
        const continuesRun =
            current &&
            slot.order_limit === current.orderLimit &&
            (current.interval === null || gap === current.interval);

        if (continuesRun) {
            if (current.interval === null) current.interval = gap;
            current.endMinutes = slot.minutes;
            current.lastMinutes = slot.minutes;
            current.slotIds.push(slot.id);
        } else {
            if (current) ranges.push(current);
            current = {
                startMinutes: slot.minutes,
                endMinutes: slot.minutes,
                lastMinutes: slot.minutes,
                interval: null,
                orderLimit: slot.order_limit,
                slotIds: [slot.id],
            };
        }
    }
    if (current) ranges.push(current);
    return ranges;
};

const OperatingHoursPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [activeType, setActiveType] = useState('delivery');
    const [slotsByType, setSlotsByType] = useState({});
    const [reservationLimitType, setReservationLimitType] = useState('guests');
    const [savingLimitType, setSavingLimitType] = useState(false);

    const [form, setForm] = useState({
        days: [],
        startTime: '11:00',
        endTime: '21:00',
        intervalMinutes: 15,
        orderLimit: 1000,
    });
    const [submitting, setSubmitting] = useState(false);
    const [removingKey, setRemovingKey] = useState(null);

    const loadAll = async () => {
        try {
            const res = await callAPI(apiUrls.TIME_SLOTS_GET_ALL, {}, 'GET');
            const data = res?.data?.data;
            setSlotsByType(data?.slots || {});
            setReservationLimitType(data?.reservation_limit_type || 'guests');
        } catch (e) {
            toast.error('Failed to load operating hours');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
            navigate('/');
            return;
        }
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const toggleFormDay = (day) => {
        setForm((prev) => ({
            ...prev,
            days: prev.days.includes(day) ? prev.days.filter((d) => d !== day) : [...prev.days, day],
        }));
    };

    const handleAddHours = async (e) => {
        e.preventDefault();
        if (form.days.length === 0) {
            toast.error('Select at least one day.');
            return;
        }
        setSubmitting(true);
        try {
            const res = await callAPI(
                apiUrls.TIME_SLOTS_SAVE,
                {},
                'POST',
                {
                    type: activeType,
                    days: form.days,
                    start_time: form.startTime,
                    end_time: form.endTime,
                    interval_minutes: form.intervalMinutes,
                    order_limit: form.orderLimit,
                },
                {}
            );
            toast.success(res?.data?.message || 'Hours added.');
            await loadAll();
        } catch (e) {
            toast.error(e?.response?.data?.message || 'Could not add those hours. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveRange = async (day, range) => {
        const key = `${day}-${range.startMinutes}-${range.endMinutes}`;
        setRemovingKey(key);
        try {
            await callAPI(
                apiUrls.TIME_SLOTS_REMOVE_RANGE,
                {},
                'POST',
                {
                    type: activeType,
                    days: [day],
                    start_time: minutesToHHMM(range.startMinutes),
                    end_time: minutesToHHMM(range.endMinutes),
                },
                {}
            );
            await loadAll();
        } catch (e) {
            toast.error('Could not remove those hours. Please try again.');
        } finally {
            setRemovingKey(null);
        }
    };

    const handleLimitTypeChange = async (nextType) => {
        const prevType = reservationLimitType;
        setReservationLimitType(nextType);
        setSavingLimitType(true);
        try {
            await callAPI(apiUrls.TIME_SLOTS_UPDATE_RESERVATION_LIMIT_TYPE, {}, 'POST', { reservation_limit_type: nextType }, {});
        } catch (e) {
            setReservationLimitType(prevType);
            toast.error('Could not save that setting. Please try again.');
        } finally {
            setSavingLimitType(false);
        }
    };

    const daysForType = slotsByType[activeType] || {};

    return (
        <MainLayout>
            <div className="pageContent">
                <div className="container">
                    <h2 className="lgTitle mb-3">Operating Hours</h2>

                    <div className="ohTabs">
                        {ORDER_TYPES.map((t) => (
                            <button
                                key={t.key}
                                type="button"
                                className={`ohTab ${activeType === t.key ? 'active' : ''}`}
                                onClick={() => setActiveType(t.key)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {activeType === 'reservation' && (
                        <div className="ohLimitTypeRow">
                            <span>Reservation limit is based on:</span>
                            <div className="form-check form-check-inline mb-0">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    id="limitByGuests"
                                    checked={reservationLimitType === 'guests'}
                                    disabled={savingLimitType}
                                    onChange={() => handleLimitTypeChange('guests')}
                                />
                                <label className="form-check-label" htmlFor="limitByGuests">Guests</label>
                            </div>
                            <div className="form-check form-check-inline mb-0">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    id="limitByBookings"
                                    checked={reservationLimitType === 'bookings'}
                                    disabled={savingLimitType}
                                    onChange={() => handleLimitTypeChange('bookings')}
                                />
                                <label className="form-check-label" htmlFor="limitByBookings">Bookings</label>
                            </div>
                        </div>
                    )}

                    <form className="ohAddCard" onSubmit={handleAddHours}>
                        <div className="ohAddRow">
                            <div className="ohField">
                                <label>Days</label>
                                <div className="ohDayPicker">
                                    {DAY_NUMBERS.map((day) => (
                                        <button
                                            key={day}
                                            type="button"
                                            className={`ohDayBtn ${form.days.includes(day) ? 'active' : ''}`}
                                            onClick={() => toggleFormDay(day)}
                                        >
                                            {DAY_SHORT[day]}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="ohField">
                                <label>Start time</label>
                                <input
                                    type="time"
                                    className="form-control"
                                    value={form.startTime}
                                    onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
                                    required
                                />
                            </div>
                            <div className="ohField">
                                <label>End time</label>
                                <input
                                    type="time"
                                    className="form-control"
                                    value={form.endTime}
                                    onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))}
                                    required
                                />
                            </div>
                            <div className="ohField">
                                <label>Every (minutes)</label>
                                <input
                                    type="number"
                                    min="1"
                                    className="form-control"
                                    style={{ width: 100 }}
                                    value={form.intervalMinutes}
                                    onChange={(e) => setForm((p) => ({ ...p, intervalMinutes: parseInt(e.target.value, 10) || '' }))}
                                    required
                                />
                            </div>
                            <div className="ohField">
                                <label>Order limit</label>
                                <input
                                    type="number"
                                    min="1"
                                    className="form-control"
                                    style={{ width: 110 }}
                                    value={form.orderLimit}
                                    onChange={(e) => setForm((p) => ({ ...p, orderLimit: parseInt(e.target.value, 10) || '' }))}
                                    required
                                />
                            </div>
                            <button type="submit" className="btn btnPrimary smBtn" disabled={submitting}>
                                {submitting ? 'Adding…' : 'Add Hours'}
                            </button>
                        </div>
                    </form>

                    {loading ? (
                        <p>Loading…</p>
                    ) : (
                        DAY_NUMBERS.map((day) => {
                            const ranges = collapseSlots(daysForType[day] || []);
                            return (
                                <div className="ohDayRow" key={day}>
                                    <div className="ohDayName">{DAY_SHORT[day]}</div>
                                    {ranges.length === 0 ? (
                                        <div className="ohEmptyDay">No hours set</div>
                                    ) : (
                                        <div className="ohChips">
                                            {ranges.map((range) => {
                                                const key = `${day}-${range.startMinutes}-${range.endMinutes}`;
                                                const label =
                                                    range.startMinutes === range.endMinutes
                                                        ? minutesToLabel(range.startMinutes)
                                                        : `${minutesToLabel(range.startMinutes)} – ${minutesToLabel(range.endMinutes)} every ${range.interval} min`;
                                                return (
                                                    <span className="ohChip" key={key}>
                                                        {label} (limit {range.orderLimit})
                                                        <button
                                                            type="button"
                                                            className="ohChipRemove"
                                                            disabled={removingKey === key}
                                                            onClick={() => handleRemoveRange(day, range)}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </MainLayout>
    );
};

export default OperatingHoursPage;
